/**
 * AppConfig Service
 * 
 * ดึงและบันทึกการตั้งค่าระบบจาก Parse Server
 * ⚠️ ระวัง: ใช้ merge เพื่อไม่ให้ข้อมูลเดิมหาย
 */

import { getCurrentParseConfig } from '@/services/adapters/parse/parse-client'
import type { AppConfig } from './app-config.types'
import {
  DEFAULT_AGENT_INFO,
  DEFAULT_DEPOSIT_INFO,
  DEFAULT_WITHDRAW_INFO,
  DEFAULT_COMMISSION_INFO,
  DEFAULT_WEBBANK_INFO,
  DEFAULT_SLIP_INFO,
  DEFAULT_WEBSITE_INFO,
  DEFAULT_CURRENCY_CONFIGS,
  DEFAULT_FEATURE_FLAGS,
  DEFAULT_AI_AGENT_CONFIG,
  DEFAULT_SECURITY_CONFIG,
  DEFAULT_TELEGRAM_CONFIG,
} from './app-config.types'

// ============================================
// Service Interface
// ============================================
export interface IAppConfigService {
  getAppConfig(): Promise<AppConfig | null>
  saveAppConfig(config: Partial<AppConfig>): Promise<AppConfig>
  refreshCache(): Promise<{ success: boolean; message?: string }>
  getHistory(limit?: number): Promise<any[]>
}

// ============================================
// Parse V1 Implementation
// ============================================
class ParseV1AppConfigService implements IAppConfigService {
  
  private getHeaders(sessionToken?: string | null): HeadersInit {
    const config = getCurrentParseConfig();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      'X-Parse-Application-Id': config.appId,
    }

    if (sessionToken) {
      headers['X-Parse-Session-Token'] = sessionToken
    } else if (config.masterKey) {
      headers['X-Parse-Master-Key'] = config.masterKey
    }

    return headers
  }

  private getSessionToken(): string | null {
    return localStorage.getItem('sessionToken')
  }

  async getAppConfig(): Promise<AppConfig | null> {
    try {
      const config = getCurrentParseConfig();
      console.log('📡 [AppConfig] Fetching from:', `${config.serverURL}/classes/AppConfig`)
      
      const response = await fetch(`${config.serverURL}/classes/AppConfig?limit=1`, {
        method: 'GET',
        headers: this.getHeaders(this.getSessionToken()),
      })

      console.log('📡 [AppConfig] Response status:', response.status)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('❌ [AppConfig] Failed to fetch:', response.status, errorData)
        return null
      }

      const data = await response.json()
      console.log('📡 [AppConfig] Raw data:', data)
      
      if (!data.results || data.results.length === 0) {
        console.log('⚠️ [AppConfig] No config found in database')
        return null
      }

      const parseConfig = data.results[0]
      console.log('✅ [AppConfig] Found config with objectId:', parseConfig.objectId)
      
      return this.mapParseToUI(parseConfig)
    } catch (error) {
      console.error('💥 [AppConfig] Error:', error)
      return null
    }
  }

  async saveAppConfig(config: Partial<AppConfig>): Promise<AppConfig> {
    try {
      console.log('💾 [AppConfig] Saving config...')
      
      // ⚠️ สำคัญ: ดึงข้อมูลเดิมมา merge ก่อน
      const existingConfig = await this.getAppConfig()
      
      if (!existingConfig) {
        throw new Error('ไม่พบการตั้งค่าในระบบ กรุณาติดต่อผู้ดูแลระบบ')
      }

      const objectId = existingConfig.objectId
      console.log('📝 [AppConfig] Updating existing config:', objectId)

      // ⚠️ MERGE: รวมข้อมูลเดิมกับข้อมูลใหม่
      const mergedConfig = this.mergeConfig(existingConfig, config)
      const parseData = this.mapUIToParse(mergedConfig)
      
      console.log('📝 [AppConfig] Data to save:', JSON.stringify(parseData, null, 2))

      const parseConfig = getCurrentParseConfig();
      const response = await fetch(`${parseConfig.serverURL}/classes/AppConfig/${objectId}`, {
        method: 'PUT',
        headers: this.getHeaders(this.getSessionToken()),
        body: JSON.stringify(parseData),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        console.error('❌ [AppConfig] Save failed:', errorData)
        throw new Error(errorData.error || 'Failed to save config')
      }

      const result = await response.json()
      console.log('✅ [AppConfig] Save result:', result)
      
      // Fetch updated config
      const updatedConfig = await this.getAppConfig()
      return updatedConfig!
    } catch (error) {
      console.error('💥 [AppConfig] Save error:', error)
      throw error
    }
  }

  /**
   * ⚠️ MERGE: รวมข้อมูลเดิมกับข้อมูลใหม่
   * - ข้อมูลใหม่จะ override ข้อมูลเดิม
   * - ข้อมูลที่ไม่ได้ส่งมาจะคงเดิม
   */
  private mergeConfig(existing: AppConfig, updates: Partial<AppConfig>): Partial<AppConfig> {
    const merged: Partial<AppConfig> = {}

    // Agent Info - merge field by field
    if (updates.agentInfo) {
      merged.agentInfo = {
        ...existing.agentInfo,
        ...updates.agentInfo,
      }
    }

    // Deposit Info
    if (updates.depositInfo) {
      merged.depositInfo = {
        ...existing.depositInfo,
        ...updates.depositInfo,
      }
    }

    // Withdraw Info
    if (updates.withdrawInfo) {
      merged.withdrawInfo = {
        ...existing.withdrawInfo,
        ...updates.withdrawInfo,
      }
    }

    // Commission
    if (updates.commission) {
      merged.commission = {
        ...existing.commission,
        ...updates.commission,
      }
    }

    // Webbank Info
    if (updates.webbankInfo) {
      merged.webbankInfo = {
        ...existing.webbankInfo,
        ...updates.webbankInfo,
      }
    }

    // Slip Info
    if (updates.slipInfo) {
      merged.slipInfo = {
        ...existing.slipInfo,
        ...updates.slipInfo,
      }
    }

    // Website Info
    if (updates.websiteInfo) {
      merged.websiteInfo = {
        ...existing.websiteInfo,
        ...updates.websiteInfo,
      }
    }

    // ✅ Feature Flags (V2)
    if (updates.featureFlags) {
      merged.featureFlags = {
        ...(existing.featureFlags || DEFAULT_FEATURE_FLAGS),
        ...updates.featureFlags,
      }
    }

    // ✅ AI Agent Config
    if (updates.aiAgentConfig) {
      merged.aiAgentConfig = {
        ...(existing.aiAgentConfig || DEFAULT_AI_AGENT_CONFIG),
        ...updates.aiAgentConfig,
      }
    }

    // ✅ Security Config
    if (updates.securityConfig) {
      merged.securityConfig = {
        ...(existing.securityConfig || DEFAULT_SECURITY_CONFIG),
        ...updates.securityConfig,
      }
    }

    // ✅ Telegram Config
    if (updates.telegramConfig) {
      merged.telegramConfig = {
        ...(existing.telegramConfig || DEFAULT_TELEGRAM_CONFIG),
        ...updates.telegramConfig,
      }
    }

    // Currency (simple values, just update if provided)
    if (updates.currency_mode !== undefined) {
      merged.currency_mode = updates.currency_mode
    }
    if (updates.currencyConfigs) {
      merged.currencyConfigs = updates.currencyConfigs
    }

    console.log('🔀 [AppConfig] Merged config:', merged)
    return merged
  }

  async refreshCache(): Promise<{ success: boolean; message?: string }> {
    try {
      const config = getCurrentParseConfig();
      console.log('🔄 [AppConfig] Refreshing cache...')
      
      const response = await fetch(`${config.serverURL}/functions/refreshAppConfigCache`, {
        method: 'POST',
        headers: this.getHeaders(this.getSessionToken()),
        body: JSON.stringify({}),
      })

      const result = await response.json()
      console.log('🔄 [AppConfig] Cache refresh result:', result)
      
      return {
        success: result.result?.success || false,
        message: result.result?.message,
      }
    } catch (error) {
      console.error('💥 [AppConfig] Refresh cache error:', error)
      return { success: false, message: 'Failed to refresh cache' }
    }
  }

  async getHistory(limit = 20): Promise<any[]> {
    try {
      const config = getCurrentParseConfig();
      const response = await fetch(`${config.serverURL}/functions/getAppConfigHistory`, {
        method: 'POST',
        headers: this.getHeaders(this.getSessionToken()),
        body: JSON.stringify({ limit }),
      })

      const result = await response.json()
      return result.result?.logs || []
    } catch (error) {
      console.error('💥 [AppConfig] Get history error:', error)
      return []
    }
  }

  private mapParseToUI(parseData: any): AppConfig {
    return {
      objectId: parseData.objectId,
      agentInfo: parseData.agentInfo || DEFAULT_AGENT_INFO,
      depositInfo: parseData.depositInfo || DEFAULT_DEPOSIT_INFO,
      withdrawInfo: parseData.withdrawInfo || DEFAULT_WITHDRAW_INFO,
      commission: parseData.commission || DEFAULT_COMMISSION_INFO,
      webbankInfo: parseData.webbankInfo || DEFAULT_WEBBANK_INFO,
      slipInfo: parseData.slipInfo || DEFAULT_SLIP_INFO,
      websiteInfo: parseData.websiteInfo || DEFAULT_WEBSITE_INFO,
      featureFlags: parseData.featureFlags || DEFAULT_FEATURE_FLAGS,  // ✅ V2 Feature Flags
      aiAgentConfig: parseData.aiAgentConfig || DEFAULT_AI_AGENT_CONFIG,  // ✅ AI Agent Config
      securityConfig: parseData.securityConfig || DEFAULT_SECURITY_CONFIG,  // ✅ Security Config
      telegramConfig: parseData.telegramConfig || DEFAULT_TELEGRAM_CONFIG,  // ✅ Telegram Config
      currency_mode: parseData.currency_mode || 'TH',
      currencyConfigs: parseData.currencyConfigs || DEFAULT_CURRENCY_CONFIGS,
      updatedAt: parseData.updatedAt,
      createdAt: parseData.createdAt,
    }
  }

  private mapUIToParse(config: Partial<AppConfig>): any {
    const parseData: any = {}

    // เฉพาะ fields ที่มีค่าเท่านั้น
    if (config.agentInfo) parseData.agentInfo = config.agentInfo
    if (config.depositInfo) parseData.depositInfo = config.depositInfo
    if (config.withdrawInfo) parseData.withdrawInfo = config.withdrawInfo
    if (config.commission) parseData.commission = config.commission
    if (config.webbankInfo) parseData.webbankInfo = config.webbankInfo
    if (config.slipInfo) parseData.slipInfo = config.slipInfo
    if (config.websiteInfo) parseData.websiteInfo = config.websiteInfo
    if (config.featureFlags) parseData.featureFlags = config.featureFlags  // ✅ V2 Feature Flags
    if (config.aiAgentConfig) parseData.aiAgentConfig = config.aiAgentConfig  // ✅ AI Agent Config
    if (config.securityConfig) parseData.securityConfig = config.securityConfig  // ✅ Security Config
    if (config.telegramConfig) parseData.telegramConfig = config.telegramConfig  // ✅ Telegram Config
    if (config.currency_mode !== undefined) parseData.currency_mode = config.currency_mode
    if (config.currencyConfigs) parseData.currencyConfigs = config.currencyConfigs

    return parseData
  }
}

// ============================================
// Mock Implementation (for testing without Parse)
// ============================================
// @ts-ignore - Mock service kept for future testing
class _MockAppConfigService implements IAppConfigService {
  private mockConfig: AppConfig = {
    objectId: 'mock-config-1',
    agentInfo: {
      AGENT_USERNAME: 'DEMO_AGENT',
      AGENT_KEY: '****',
      AGENT_WEB: 'https://demo-agent.com',
      AGENT_PREFIX: 'DEMO',
      AGENT_TEMPLATE: 'default',
    },
    depositInfo: {
      deposit_min: 100,
      deposit_max: 500000,
      deposit_min_with_slip: 100,
      deposit_turnover_bonus_x: 5,
      deposit_turnover_money_x: 1,
      deposit_turnover_reset_below: 5,
    },
    withdrawInfo: {
      withdraw_min: 300,
      withdraw_max: 200000,
      withdraw_after_deposit_time: 5,
      withdraw_after_deposit_percent: 50,
      withdraw_limit: 5,
      withdraw_max_perday: 5,
      withdraw_max_perday_amount: 1000000,
      autoButton: [100, 200, 300, 500, 1000],
    },
    commission: {
      m1_percent: 5,
      m2_percent: 2,
      p1_percent: 10,
      p2_percent: 5,
      count_from: 'deposit',
      return_to: 'credit',
      withdraw_min: 100,
      message: 'แนะนำเพื่อนรับโบนัสทันที!',
    },
    webbankInfo: {
      truewallet_endpoint_url: 'https://api.truewallet.com',
      truewallet_key: '****',
      truewallet_first_verify: true,
    },
    slipInfo: {
      easyslip_endpoint_url: 'https://api.easyslip.com',
      easyslip_key: '****',
      easyslip_enabled: true,
      slip2go_endpoint_url: 'https://api.slip2go.com',
      slip2go_key: '****',
      slip2go_enabled: false,
    },
    websiteInfo: {
      siteName: 'WINNER99',
      publicUrl: 'https://winner99.com',
      publicFrontUrl: 'https://play.winner99.com',
      publicFrontApi: 'https://api.winner99.com',
      publicUrlSignup: 'https://winner99.com/signup',
    },
    currency_mode: 'TH',
    currencyConfigs: DEFAULT_CURRENCY_CONFIGS,
    updatedAt: new Date().toISOString(),
    createdAt: new Date().toISOString(),
  }

  async getAppConfig(): Promise<AppConfig | null> {
    await new Promise(resolve => setTimeout(resolve, 500))
    return this.mockConfig
  }

  async saveAppConfig(config: Partial<AppConfig>): Promise<AppConfig> {
    await new Promise(resolve => setTimeout(resolve, 500))
    
    // Merge properly
    if (config.agentInfo) {
      this.mockConfig.agentInfo = { ...this.mockConfig.agentInfo, ...config.agentInfo }
    }
    if (config.depositInfo) {
      this.mockConfig.depositInfo = { ...this.mockConfig.depositInfo, ...config.depositInfo }
    }
    if (config.withdrawInfo) {
      this.mockConfig.withdrawInfo = { ...this.mockConfig.withdrawInfo, ...config.withdrawInfo }
    }
    if (config.commission) {
      this.mockConfig.commission = { ...this.mockConfig.commission, ...config.commission }
    }
    if (config.webbankInfo) {
      this.mockConfig.webbankInfo = { ...this.mockConfig.webbankInfo, ...config.webbankInfo }
    }
    if (config.slipInfo) {
      this.mockConfig.slipInfo = { ...this.mockConfig.slipInfo, ...config.slipInfo }
    }
    if (config.websiteInfo) {
      this.mockConfig.websiteInfo = { ...this.mockConfig.websiteInfo, ...config.websiteInfo }
    }
    if (config.currency_mode !== undefined) {
      this.mockConfig.currency_mode = config.currency_mode
    }
    if (config.currencyConfigs) {
      this.mockConfig.currencyConfigs = config.currencyConfigs
    }
    
    this.mockConfig.updatedAt = new Date().toISOString()
    return this.mockConfig
  }

  async refreshCache(): Promise<{ success: boolean; message?: string }> {
    await new Promise(resolve => setTimeout(resolve, 300))
    return { success: true, message: 'Cache refreshed successfully' }
  }

  async getHistory(_limit = 20): Promise<any[]> {
    await new Promise(resolve => setTimeout(resolve, 300))
    return [
      {
        id: '1',
        action: 'update',
        actorName: 'Admin',
        description: 'อัปเดตการตั้งค่าระบบ (3 รายการ)',
        timestamp: new Date(Date.now() - 3600000).toISOString(),
        changes: [
          { field: 'depositInfo.deposit_min', oldValue: '50', newValue: '100' },
          { field: 'withdrawInfo.withdraw_min', oldValue: '200', newValue: '300' },
        ],
      },
    ]
  }
}

// ============================================
// Factory - Always use Parse V1 in production
// ============================================
let serviceInstance: IAppConfigService | null = null

export function getAppConfigService(): IAppConfigService {
  if (!serviceInstance) {
    // ใช้ Parse V1 เสมอ (เหมือน User Service)
    console.log('🏭 [AppConfig] Creating ParseV1AppConfigService')
    serviceInstance = new ParseV1AppConfigService()
  }
  
  return serviceInstance
}

// Reset service (for testing)
export function resetAppConfigService(): void {
  serviceInstance = null
}
